import { useEffect, useRef } from "react";
import {
  selectPeers,
  useHMSStore,
  useRecordingStreaming,
} from "@100mslive/react-sdk";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { SIDE_PANES, useSidePane } from "@/hooks/useSidePane";
import { isInternalRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

/**
 * Shown to everyone whenever the room is being recorded, whoever started it.
 * The 100ms template records every session on its own (inventory 1.3), so
 * this is the one place participants learn about it when the platform did
 * not ask for a recording.
 */
export const RecordingBadge = () => {
  const { isRecordingOn } = useRecordingStreaming();
  const announced = useRef(false);

  useEffect(() => {
    if (isRecordingOn && !announced.current) {
      announced.current = true;
      toast("Interview recording has started");
    }
  }, [isRecordingOn]);

  if (!isRecordingOn) {
    return null;
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs font-medium text-foreground"
      data-testid="recording_badge"
    >
      <span className="size-2 animate-pulse rounded-full bg-destructive" />
      Recording
    </span>
  );
};

const ParticipantsButton = () => {
  const count = useHMSStore(selectPeers).filter(
    peer => !isInternalRole(peer.roleName)
  ).length;
  const { sidePane, toggleSidePane } = useSidePane();
  const isOpen = sidePane === SIDE_PANES.participants;
  return (
    <Button
      variant="ghost"
      size="sm"
      data-testid="participant_list"
      aria-pressed={isOpen}
      className={cn(
        "text-muted-foreground",
        isOpen && "bg-accent text-foreground"
      )}
      onClick={() => toggleSidePane(SIDE_PANES.participants)}
    >
      <Users aria-hidden="true" />
      {/* Named by its content ("Participants 2"), so the visible count is
          part of the name (WCAG 2.5.3). */}
      <span className="sr-only">Participants</span>
      {count}
    </Button>
  );
};

export const MeetingHeader = () => (
  <header
    className="flex h-12 shrink-0 items-center justify-between gap-3 border-b bg-card px-4"
    data-testid="header"
  >
    <Logo className="h-5" />
    <div className="flex items-center gap-2">
      <RecordingBadge />
      <ParticipantsButton />
    </div>
  </header>
);
